import React from "react";
import { Box } from "@mui/material";
import useTheme from "../../../../common/hooks/useTheme";
import { Theme } from "../../../../providers/ThemeProvider";

/**
 * "{step} to {total} steps" caption over a segmented progress bar — one pill
 * per step, filled up to the current one. The same indicator as
 * `SegmentedStepper` (the Logic / Service / Lab builder wizards), extracted
 * for modals that own their footer: the builders' stepper renders fixed
 * Previous / Next / Save buttons, while the Add-results wizards (InBody sync,
 * Keragon "Sync EHR data") need "Search records" / "Back | Import".
 */
type Props = {
  step: number;
  total: number;
};

const StepProgressBar = ({ step, total }: Props) => {
  const theme: Theme = useTheme();
  const count = Math.max(total, 1);

  return (
    <Box sx={styles.container}>
      <Box sx={styles.label(theme)}>
        {step} to {total} steps
      </Box>
      <Box sx={styles.track}>
        {Array.from({ length: count }, (_, index) => (
          <Box
            key={index}
            sx={styles.segment(theme, index < step)}
            aria-hidden
          />
        ))}
      </Box>
    </Box>
  );
};

export default StepProgressBar;

const styles = {
  container: {
    display: "flex",
    flexDirection: "column" as "column",
    gap: "8px",
  },
  label: (theme: Theme) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "12px",
    lineHeight: "17px",
    letterSpacing: "-0.24px",
    color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
  }),
  track: {
    display: "flex",
    gap: "4px",
    width: "100%",
  },
  segment: (theme: Theme, filled: boolean) => ({
    height: "4px",
    flex: 1,
    borderRadius: "16px",
    backgroundColor: filled
      ? theme.colors_ds.PRIMARY_ICON_MAIN
      : theme.colors_ds.NEUTRAL_BORDER_WEAK,
    transition: "background-color 0.3s ease-in-out",
  }),
};
